import { EmptyState, Surface } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareWarning, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { formatDate, formatDateTime } from '../../../lib/format';
import { errorMessage } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { claimKeys, fetchClaims } from '../api/claims-api';
import { ClaimStatusBadge } from '../components/ClaimStatusBadge';

/**
 * `/portal/claims` — the restaurant's own claims.
 *
 * Deliberately not a DataTable: this is a phone in a busy kitchen, so it is a
 * stack of large tap targets with the status and the date up front. The rows
 * are already restaurant-scoped by the server; the portal never asks for
 * "all claims" and gets filtered.
 */
export function PortalClaimsPage(): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const listState = { page: 1, pageSize: 50, sort: 'createdAt', dir: 'desc' as const };

  const query = useQuery({ queryKey: claimKeys.list(listState), queryFn: () => fetchClaims(listState) });
  useLiveQuery(['claims'], claimKeys.all);

  const items = query.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title={t('claims:portal.title')}
        subtitle={t('claims:portal.subtitle')}
        actions={
          <Link
            to="/portal/claims/new"
            className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            <Plus className="size-4" aria-hidden />
            {t('claims:portal.new')}
          </Link>
        }
      />

      {query.isLoading ? <FullPageSpinner /> : null}
      {query.error ? <p className="text-danger">{errorMessage(query.error)}</p> : null}

      {!query.isLoading && items.length === 0 ? (
        <EmptyState
          icon={<MessageSquareWarning />}
          title={t('claims:portal.empty')}
          description={t('claims:portal.emptyDescription')}
        />
      ) : (
        <ul className="space-y-3">
          {items.map((claim) => (
            <li key={claim.id}>
              <Link to={`/portal/claims/${claim.id}`} className="block">
                <Surface variant="card" className="transition-colors hover:bg-brand-600/[0.04]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-sm text-ink">{claim.claimNo}</span>
                    <ClaimStatusBadge status={claim.status} />
                  </div>
                  <p className="mt-1 text-sm text-ink-light">{t(`claims:reason.${claim.reason}`)}</p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {claim.salesOrderNo ? `${claim.salesOrderNo} · ${formatDate(claim.deliveryDate)} · ` : ''}
                    {t('claims:list.lineCount', { count: claim.lineCount })} ·{' '}
                    {formatDateTime(claim.createdAt)}
                  </p>
                </Surface>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-6 text-xs text-ink-muted">{t('claims:portal.statusHelp')}</p>
    </div>
  );
}
